import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Slider } from '@/components/ui/slider'
import { Plus, Trash2, RefreshCw, Check, AlertCircle, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { WorkflowVisualizer } from '@/modules/project-hub/components/WorkflowVisualizer'
import { PromptTemplatesPanel } from '@/modules/admin/components/PromptTemplatesPanel'

export const Route = createFileRoute('/admin/llm')({
  component: LLMConfigPage,
})

// 场景列表
const SCENES = [
  { id: 'summary', name: '文档摘要', description: '分析文档类型、主题和风格' },
  { id: 'term_identification', name: '术语预识别', description: '识别文档中的关键术语和固定搭配' },
  { id: 'translation', name: '整篇初译', description: '执行初始翻译' },
  { id: 'reflection', name: '自我修正', description: '基于MQM评估进行自我修正' },
  { id: 'mqm', name: 'MQM质量评分', description: '多维度质量评估' },
  { id: 'feedback_extraction', name: '反馈规律提取', description: '从客户反馈提取规律' },
  { id: 'rag_vectorization', name: 'RAG向量化', description: '生成向量嵌入' },
  { id: 'vision', name: '图片解析', description: '解析图片反馈中的文字和图表（多模态视觉模型）' },
]

function LLMConfigPage() {
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState('providers')

  // 权限检查：只有创始人能访问
  if (!user) {
    return <Navigate to="/login" />
  }

  if (user.role !== 'founder') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              访问受限
            </CardTitle>
            <CardDescription>
              只有创始人角色才能访问 AI 模型配置页面
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              您的当前角色：<Badge variant="secondary">{user.role}</Badge>
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">AI 模型配置</h1>
        <p className="text-muted-foreground">管理 LLM 供应商和场景模型分配</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="providers">供应商管理</TabsTrigger>
          <TabsTrigger value="scenes">场景配置</TabsTrigger>
          <TabsTrigger value="optimize">Prompt外显</TabsTrigger>
          <TabsTrigger value="workflow">工作流可视化</TabsTrigger>
        </TabsList>

        <TabsContent value="providers" className="space-y-4">
          <ProviderManager />
        </TabsContent>

        <TabsContent value="scenes" className="space-y-4">
          <SceneConfigManager />
        </TabsContent>

        <TabsContent value="optimize" className="space-y-4">
          <PromptTemplatesPanel />
        </TabsContent>

        <TabsContent value="workflow" className="space-y-4">
          <WorkflowVisualizer />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// 供应商管理组件
function ProviderManager() {
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [newProvider, setNewProvider] = useState({
    name: '',
    provider: 'openai',
    baseUrl: 'https://api.openai.com/v1',
    apiKey: '',
  })

  const { data: providers, isLoading } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: async () => {
      const res = await api.get('/llm-config/providers')
      return res.data
    },
  })

  const addMutation = useMutation({
    mutationFn: async (data: typeof newProvider) => {
      const res = await api.post('/llm-config/providers', data)
      return res.data
    },
    onSuccess: () => {
      toast.success('供应商添加成功')
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] })
      setShowAddForm(false)
      setNewProvider({ name: '', provider: 'openai', baseUrl: '', apiKey: '' })
    },
    onError: (error: any) => {
      toast.error(error.error || '添加失败')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/llm-config/providers/${id}`)
      return res.data
    },
    onSuccess: () => {
      toast.success('供应商已删除')
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] })
    },
    onError: (error: any) => {
      toast.error(error.error || '删除失败')
    },
  })

  const refreshModelsMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/llm-config/providers/${id}/refresh-models`)
      return res.data
    },
    onSuccess: (data) => {
      toast.success(`成功获取 ${data.count} 个模型`)
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] })
    },
    onError: (error: any) => {
      toast.error(error.error || '刷新失败')
    },
  })

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({
    name: '',
    provider: 'openai',
    baseUrl: '',
    apiKey: '',
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof editForm }) => {
      const res = await api.put(`/llm-config/providers/${id}`, data)
      return res.data
    },
    onSuccess: () => {
      toast.success('供应商已更新')
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] })
      setEditingId(null)
    },
    onError: (error: any) => {
      toast.error(error.error || '更新失败')
    },
  })

  const startEditing = (provider: any) => {
    setEditingId(provider.id)
    setEditForm({
      name: provider.name || '',
      provider: provider.provider || 'openai',
      baseUrl: provider.baseUrl || '',
      apiKey: '',
    })
  }

  const handleEditSubmit = (e: React.FormEvent, id: string) => {
    e.preventDefault()
    // 只发送有值的字段，apiKey 为空则不更新
    const data: Record<string, string> = {}
    if (editForm.name) data.name = editForm.name
    if (editForm.provider) data.provider = editForm.provider
    if (editForm.baseUrl) data.baseUrl = editForm.baseUrl
    if (editForm.apiKey) data.apiKey = editForm.apiKey
    updateMutation.mutate({ id, data: data as any })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    addMutation.mutate(newProvider)
  }

  if (isLoading) {
    return <div>加载中...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">LLM 供应商</h2>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="w-4 h-4 mr-2" />
          添加供应商
        </Button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>添加新供应商</CardTitle>
            <CardDescription>配置 OpenAI 兼容的 API 端点</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>名称</Label>
                  <Input
                    placeholder="例如：OpenAI"
                    value={newProvider.name}
                    onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>提供商类型</Label>
                  <Select
                    value={newProvider.provider}
                    onValueChange={(value) => setNewProvider({ ...newProvider, provider: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="azure">Azure OpenAI</SelectItem>
                      <SelectItem value="anthropic">Anthropic</SelectItem>
                      <SelectItem value="custom">自定义</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Base URL</Label>
                <Input
                  placeholder="https://api.openai.com/v1"
                  value={newProvider.baseUrl}
                  onChange={(e) => setNewProvider({ ...newProvider, baseUrl: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>API Key</Label>
                <Input
                  type="password"
                  placeholder="sk-..."
                  value={newProvider.apiKey}
                  onChange={(e) => setNewProvider({ ...newProvider, apiKey: e.target.value })}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={addMutation.isPending}>
                  {addMutation.isPending ? '添加中...' : '添加'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                  取消
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {providers?.map((provider: any) => (
          <Card key={provider.id}>
            <CardContent className="pt-6">
              {editingId === provider.id ? (
                <form onSubmit={(e) => handleEditSubmit(e, provider.id)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>名称</Label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>连接方式</Label>
                      <Select
                        value={editForm.provider}
                        onValueChange={(value) => setEditForm({ ...editForm, provider: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="openai">OpenAI</SelectItem>
                          <SelectItem value="azure">Azure OpenAI</SelectItem>
                          <SelectItem value="anthropic">Anthropic</SelectItem>
                          <SelectItem value="custom">自定义</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Base URL</Label>
                    <Input
                      value={editForm.baseUrl}
                      onChange={(e) => setEditForm({ ...editForm, baseUrl: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      placeholder="留空则不修改"
                      value={editForm.apiKey}
                      onChange={(e) => setEditForm({ ...editForm, apiKey: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={updateMutation.isPending}>
                      {updateMutation.isPending ? '保存中...' : '保存'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setEditingId(null)}>
                      取消
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{provider.name}</h3>
                      <Badge variant={provider.isActive ? 'default' : 'secondary'}>
                        {provider.isActive ? '活跃' : '停用'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{provider.baseUrl}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {provider.models?.slice().sort((a: string, b: string) => a.localeCompare(b)).map((model: string) => (
                        <Badge key={model} variant="outline" className="text-xs">
                          {model}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditing(provider)}
                      title="编辑"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => refreshModelsMutation.mutate(provider.id)}
                      disabled={refreshModelsMutation.isPending}
                      title="刷新模型列表"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshModelsMutation.isPending ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMutation.mutate(provider.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

// 场景配置管理组件
function SceneConfigManager() {
  const queryClient = useQueryClient()
  const [editingScene, setEditingScene] = useState<string | null>(null)
  const [sceneConfig, setSceneConfig] = useState<Record<string, any>>({})

  const { data: providers } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: async () => {
      const res = await api.get('/llm-config/providers')
      return res.data
    },
  })

  const { data: scenes, isLoading } = useQuery({
    queryKey: ['llm-scenes'],
    queryFn: async () => {
      const res = await api.get('/llm-config/scenes')
      return res.data
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ scene, config }: { scene: string; config: any }) => {
      const res = await api.put(`/llm-config/scene/${scene}`, config)
      return res.data
    },
    onSuccess: () => {
      toast.success('场景配置已更新')
      queryClient.invalidateQueries({ queryKey: ['llm-scenes'] })
      setEditingScene(null)
    },
    onError: (error: any) => {
      toast.error(error.error || '更新失败')
    },
  })

  const handleSave = (sceneId: string) => {
    const config = sceneConfig[sceneId]
    if (config) {
      updateMutation.mutate({ scene: sceneId, config })
    }
  }

  const startEditing = (scene: any) => {
    setEditingScene(scene.id)
    setSceneConfig({
      ...sceneConfig,
      [scene.id]: {
        llmConfigId: scene.config?.llmConfigId || providers?.[0]?.id,
        modelName: scene.config?.modelName || 'gpt-4o',
        temperature: scene.config?.temperature ?? 0.3,
      },
    })
  }

  if (isLoading) {
    return <div>加载中...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">场景模型配置</h2>
          <p className="text-sm text-muted-foreground">为不同翻译场景分配特定的模型</p>
        </div>
      </div>

      <div className="grid gap-4">
        {SCENES.map((scene) => {
          const sceneData = scenes?.find((s: any) => s.id === scene.id)
          const isEditing = editingScene === scene.id
          
          const currentProviderId = sceneConfig[scene.id]?.llmConfigId
          const selectedProvider = providers?.find((p: any) => p.id === currentProviderId)
          const availableModels = selectedProvider?.models?.slice().sort((a: string, b: string) => a.localeCompare(b)) || []

          return (
            <Card key={scene.id}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{scene.name}</h3>
                      {sceneData?.config && (
                        <Badge variant="outline" className="text-xs">
                          <Check className="w-3 h-3 mr-1" />
                          已配置
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{scene.description}</p>

                    {isEditing ? (
                      <div className="space-y-4 bg-muted p-4 rounded-lg">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>供应商</Label>
                            <Select
                              value={sceneConfig[scene.id]?.llmConfigId}
                              onValueChange={(value) =>
                                setSceneConfig({
                                  ...sceneConfig,
                                  [scene.id]: { ...sceneConfig[scene.id], llmConfigId: value },
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {providers?.slice().sort((a: any, b: any) => a.name.localeCompare(b.name)).map((p: any) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label>模型名称</Label>
                            {availableModels.length > 0 ? (
                              <Select
                                value={sceneConfig[scene.id]?.modelName}
                                onValueChange={(value) =>
                                  setSceneConfig({
                                    ...sceneConfig,
                                    [scene.id]: { ...sceneConfig[scene.id], modelName: value },
                                  })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="选择模型" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableModels.map((model: string) => (
                                    <SelectItem key={model} value={model}>
                                      {model}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                placeholder="gpt-4o"
                                value={sceneConfig[scene.id]?.modelName}
                                onChange={(e) =>
                                  setSceneConfig({
                                    ...sceneConfig,
                                    [scene.id]: { ...sceneConfig[scene.id], modelName: e.target.value },
                                  })
                                }
                              />
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Temperature: {sceneConfig[scene.id]?.temperature}</Label>
                          <Slider
                            value={[sceneConfig[scene.id]?.temperature || 0.3]}
                            onValueChange={([value]) =>
                              setSceneConfig({
                                ...sceneConfig,
                                [scene.id]: { ...sceneConfig[scene.id], temperature: value },
                              })
                            }
                            min={0}
                            max={2}
                            step={0.1}
                          />
                        </div>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSave(scene.id)}
                            disabled={updateMutation.isPending}
                          >
                            保存
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingScene(null)}>
                            取消
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="text-sm">
                          <span className="text-muted-foreground">当前模型: </span>
                          <span className="font-medium">
                            {sceneData?.config?.modelName || '未配置（使用默认）'}
                          </span>
                        </div>
                        {sceneData?.config && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Temperature: </span>
                            <span className="font-medium">{sceneData.config.temperature}</span>
                          </div>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => startEditing(scene)}>
                          <RefreshCw className="w-4 h-4 mr-1" />
                          配置
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
